import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';

const ROLE_OPTIONS = [
  { value: 'none', label: 'Not assignable' },
  { value: 'account_management', label: 'Account Management' },
  { value: 'performance', label: 'Performance' },
  { value: 'ops', label: 'Operations' },
  { value: 'finance', label: 'Finance' },
  { value: 'creative', label: 'Creative' },
  { value: 'integrations', label: 'Integrations' },
  { value: 'lead_management', label: 'Lead Management' },
  { value: 'lead_quality', label: 'Lead Quality' },
  { value: 'executive_review', label: 'Executive Review' },
];

const CATEGORY_OPTIONS = [
  { value: '', label: 'Any category' },
  { value: 'budget_pacing', label: 'Budget pacing' },
  { value: 'channel_performance', label: 'Channel performance' },
  { value: 'vertical_performance', label: 'Vertical performance' },
  { value: 'geo_performance', label: 'Geo performance' },
  { value: 'lead_summary', label: 'Lead summary' },
  { value: 'campaign_performance', label: 'Campaign performance' },
  { value: 'adset_performance', label: 'Ad set performance' },
  { value: 'ad_performance', label: 'Ad performance' },
  { value: 'backend_outcomes', label: 'Backend outcomes' },
  { value: 'coverage', label: 'Coverage' },
  { value: 'landing_page', label: 'Landing page' },
  { value: 'offers', label: 'Offers' },
  { value: 'configuration', label: 'Configuration' },
  { value: 'action_request', label: 'Action request' },
  { value: 'ads_library', label: 'Ads library' },
  { value: 'general', label: 'General' },
];

const WORK_DECISION_OPTIONS = [
  { value: '', label: 'Any work decision' },
  { value: 'reply_and_task', label: 'Reply + task' },
  { value: 'task_only', label: 'Task only' },
  { value: 'needs_human_review', label: 'Needs human review' },
];

const EMPTY_RULE = {
  id: null,
  client: null,
  category: '',
  work_decision: '',
  assignee: null,
  priority: 100,
  is_active: true,
  notes: '',
};

function TaskAssignmentSettingsDialog({
  open,
  onClose,
  assignmentUsers,
  assignmentRules,
  clients,
  savingUserId,
  savingRuleId,
  deletingRuleId,
  onSaveUser,
  onCreateRule,
  onUpdateRule,
  onDeleteRule,
}) {
  const [tab, setTab] = useState('users');
  const [userDrafts, setUserDrafts] = useState({});
  const [ruleForm, setRuleForm] = useState(EMPTY_RULE);

  useEffect(() => {
    if (!open) {
      return;
    }
    const nextDrafts = {};
    assignmentUsers.forEach((user) => {
      nextDrafts[user.id] = {
        inbox_task_role: user.inbox_task_role || 'none',
        inbox_task_roles: Array.isArray(user.inbox_task_roles) ? user.inbox_task_roles : [],
        inbox_task_assignment_enabled: Boolean(user.inbox_task_assignment_enabled),
        inbox_task_is_default_assignee: Boolean(user.inbox_task_is_default_assignee),
      };
    });
    setUserDrafts(nextDrafts);
    setRuleForm(EMPTY_RULE);
  }, [open, assignmentUsers]);

  const assigneeOptions = useMemo(
    () => assignmentUsers.filter((user) => user.inbox_task_assignment_enabled || user.inbox_task_role !== 'none'),
    [assignmentUsers],
  );

  const handleUserChange = (userId, field, value) => {
    setUserDrafts((prev) => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        [field]: value,
      },
    }));
  };

  const handleSaveUser = (userId) => {
    const draft = userDrafts[userId];
    if (!draft) {
      return;
    }
    onSaveUser(userId, draft);
  };

  const handleRuleFieldChange = (field, value) => {
    setRuleForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEditRule = (rule) => {
    setTab('rules');
    setRuleForm({
      id: rule.id,
      client: rule.client,
      category: rule.category || '',
      work_decision: rule.work_decision || '',
      assignee: rule.assignee,
      priority: rule.priority ?? 100,
      is_active: Boolean(rule.is_active),
      notes: rule.notes || '',
    });
  };

  const handleSubmitRule = () => {
    if (!ruleForm.client || !ruleForm.assignee) {
      return;
    }
    const payload = {
      client: ruleForm.client,
      category: ruleForm.category,
      work_decision: ruleForm.work_decision,
      assignee: ruleForm.assignee,
      priority: Number(ruleForm.priority) || 100,
      is_active: Boolean(ruleForm.is_active),
      notes: ruleForm.notes,
    };
    if (ruleForm.id) {
      onUpdateRule(ruleForm.id, payload);
    } else {
      onCreateRule(payload);
    }
    setRuleForm(EMPTY_RULE);
  };

  const handleResetRule = () => {
    setRuleForm(EMPTY_RULE);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Inbox Assignment Settings</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="info">
            Manage which staff users can receive AI-routed inbox tasks and add client-specific override rules.
          </Alert>
          <Tabs value={tab} onChange={(_, value) => setTab(value)}>
            <Tab value="users" label="Staff Users" />
            <Tab value="rules" label="Client Rules" />
          </Tabs>

          {tab === 'users' && (
            <Stack spacing={2}>
              {assignmentUsers.map((user) => {
                const draft = userDrafts[user.id] || {
                  inbox_task_role: 'none',
                  inbox_task_roles: [],
                  inbox_task_assignment_enabled: false,
                  inbox_task_is_default_assignee: false,
                };
                return (
                  <Card key={user.id} variant="outlined">
                    <CardContent>
                      <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={2}
                        alignItems={{ xs: 'flex-start', md: 'center' }}
                      >
                        <Box sx={{ minWidth: 220 }}>
                          <Typography fontWeight={600}>{user.full_name || user.email}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {user.email}
                          </Typography>
                        </Box>
                        <TextField
                          select
                          label="Primary role"
                          value={draft.inbox_task_role}
                          onChange={(event) =>
                            handleUserChange(user.id, 'inbox_task_role', event.target.value)
                          }
                          sx={{ minWidth: 220 }}
                        >
                          {ROLE_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                            ))}
                          </TextField>
                        <Box sx={{ minWidth: 260 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            Additional roles
                          </Typography>
                          <Select
                            multiple
                            fullWidth
                            size="small"
                            value={draft.inbox_task_roles || []}
                            onChange={(event) =>
                              handleUserChange(
                                user.id,
                                'inbox_task_roles',
                                typeof event.target.value === 'string'
                                  ? event.target.value.split(',')
                                  : event.target.value,
                              )
                            }
                            renderValue={(selected) =>
                              Array.isArray(selected) && selected.length
                                ? selected
                                    .map((value) => ROLE_OPTIONS.find((option) => option.value === value)?.label || value)
                                    .join(', ')
                                : 'None'
                            }
                          >
                            {ROLE_OPTIONS.filter((option) => option.value !== 'none').map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </Box>
                        <FormControlLabel
                          control={(
                            <Switch
                              checked={Boolean(draft.inbox_task_assignment_enabled)}
                              onChange={(event) =>
                                handleUserChange(
                                  user.id,
                                  'inbox_task_assignment_enabled',
                                  event.target.checked,
                                )
                              }
                            />
                          )}
                          label="Enabled"
                        />
                        <FormControlLabel
                          control={(
                            <Switch
                              checked={Boolean(draft.inbox_task_is_default_assignee)}
                              onChange={(event) =>
                                handleUserChange(
                                  user.id,
                                  'inbox_task_is_default_assignee',
                                  event.target.checked,
                                )
                              }
                            />
                          )}
                          label="Default fallback"
                        />
                        <Box sx={{ flexGrow: 1 }} />
                        <Button
                          variant="contained"
                          onClick={() => handleSaveUser(user.id)}
                          disabled={savingUserId === user.id}
                        >
                          Save
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          )}

          {tab === 'rules' && (
            <Stack spacing={3}>
              <Card variant="outlined">
                <CardContent>
                  <Stack spacing={2}>
                    <Typography variant="h6">
                      {ruleForm.id ? 'Edit client rule' : 'Add client rule'}
                    </Typography>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <TextField
                        select
                        label="Client"
                        value={ruleForm.client || ''}
                        onChange={(event) => handleRuleFieldChange('client', Number(event.target.value) || null)}
                        fullWidth
                      >
                        <MenuItem value="">Select client</MenuItem>
                        {clients.map((client) => (
                          <MenuItem key={client.id} value={client.id}>
                            {client.name}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        select
                        label="Assignee"
                        value={ruleForm.assignee || ''}
                        onChange={(event) => handleRuleFieldChange('assignee', Number(event.target.value) || null)}
                        fullWidth
                      >
                        <MenuItem value="">Select assignee</MenuItem>
                        {assigneeOptions.map((user) => (
                          <MenuItem key={user.id} value={user.id}>
                            {user.full_name || user.email}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Stack>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <TextField
                        select
                        label="Category"
                        value={ruleForm.category}
                        onChange={(event) => handleRuleFieldChange('category', event.target.value)}
                        fullWidth
                      >
                        {CATEGORY_OPTIONS.map((option) => (
                          <MenuItem key={option.value || 'any'} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        select
                        label="Work decision"
                        value={ruleForm.work_decision}
                        onChange={(event) => handleRuleFieldChange('work_decision', event.target.value)}
                        fullWidth
                      >
                        {WORK_DECISION_OPTIONS.map((option) => (
                          <MenuItem key={option.value || 'any'} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        label="Priority"
                        type="number"
                        value={ruleForm.priority}
                        onChange={(event) => handleRuleFieldChange('priority', event.target.value)}
                        sx={{ maxWidth: 160 }}
                      />
                    </Stack>
                    <TextField
                      label="Notes"
                      value={ruleForm.notes}
                      onChange={(event) => handleRuleFieldChange('notes', event.target.value)}
                      multiline
                      minRows={2}
                      fullWidth
                    />
                    <FormControlLabel
                      control={(
                        <Switch
                          checked={Boolean(ruleForm.is_active)}
                          onChange={(event) => handleRuleFieldChange('is_active', event.target.checked)}
                        />
                      )}
                      label="Rule active"
                    />
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button onClick={handleResetRule}>Reset</Button>
                      <Button
                        variant="contained"
                        onClick={handleSubmitRule}
                        disabled={Boolean(savingRuleId)}
                      >
                        {ruleForm.id ? 'Save rule' : 'Add rule'}
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>

              <Divider />

              <Stack spacing={2}>
                {assignmentRules.map((rule) => (
                  <Card key={rule.id} variant="outlined">
                    <CardContent>
                      <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={2}
                        alignItems={{ xs: 'flex-start', md: 'center' }}
                      >
                        <Box sx={{ minWidth: 260 }}>
                          <Typography fontWeight={600}>{rule.client_name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {rule.category || 'Any category'} / {rule.work_decision || 'Any work decision'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Assignee: {rule.assignee_name || 'Unassigned'}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          Priority {rule.priority}
                        </Typography>
                        <Typography variant="body2" color={rule.is_active ? 'success.main' : 'text.secondary'}>
                          {rule.is_active ? 'Active' : 'Inactive'}
                        </Typography>
                        <Box sx={{ flexGrow: 1 }} />
                        <Stack direction="row" spacing={1}>
                          <Button variant="outlined" onClick={() => handleEditRule(rule)}>
                            Edit
                          </Button>
                          <Button
                            color="error"
                            onClick={() => onDeleteRule(rule.id)}
                            disabled={deletingRuleId === rule.id}
                          >
                            Delete
                          </Button>
                        </Stack>
                      </Stack>
                      {rule.notes ? (
                        <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
                          {rule.notes}
                        </Typography>
                      ) : null}
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

TaskAssignmentSettingsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  assignmentUsers: PropTypes.arrayOf(PropTypes.object).isRequired,
  assignmentRules: PropTypes.arrayOf(PropTypes.object).isRequired,
  clients: PropTypes.arrayOf(PropTypes.object).isRequired,
  savingUserId: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  savingRuleId: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  deletingRuleId: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  onSaveUser: PropTypes.func.isRequired,
  onCreateRule: PropTypes.func.isRequired,
  onUpdateRule: PropTypes.func.isRequired,
  onDeleteRule: PropTypes.func.isRequired,
};

TaskAssignmentSettingsDialog.defaultProps = {
  savingUserId: null,
  savingRuleId: null,
  deletingRuleId: null,
};

export default TaskAssignmentSettingsDialog;
